/* ──────────────────────────────────────────────────────────────────────────
 * Readiness Assessment desk — embedded IPO readiness workspace for the IPO
 * Navigator "Readiness Score" screen.
 *
 * Two real, backend-connected flows, re-skinned in the IPO Navigator look
 * (render inside a `.ipo-nav-root` ancestor — scoped CSS in IPONavigator.css):
 *
 *   1. Pre-IPO Financial Assessment — a 6-step wizard (Company Info → P&L →
 *      Balance Sheet → Projections → Governance → Market Data) that posts to the
 *      live  POST /api/assessment/calculate  engine, renders the full inline
 *      result report (readiness score, SEBI eligibility, valuation calculators,
 *      governance, AI gap analysis) and offers a downloadable PDF report.
 *
 *   2. BSE SME / NSE EMERGE Self-Assessment — the official exchange-criteria
 *      checklist (data: sme_eligibility_checklists) with an inline readiness
 *      band, a category-grouped to-do list and a branded jsPDF report — exactly
 *      mirroring the standalone /assessment/sme-self flow.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import GOVERNANCE_QUESTIONS, { GOVERNANCE_CATEGORIES } from "@/config/governanceQuestions";
import { ELIGIBILITY_CHECKLISTS, BOARD_LABELS } from "@/data/sme_eligibility_checklists";
import ContactLeadDialog from "@/components/ContactLeadDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ════════════════════════════════════════════════════════════════════════
 * Shared small presentational helpers (IPO Navigator skin)
 * ════════════════════════════════════════════════════════════════════════ */
const NumField = ({ label, value, onChange, placeholder = "0.00", suffix, testid }) => (
  <div className="ra-field">
    <label className="ra-label">{label}</label>
    <div className="ra-input-wrap">
      <span className="ra-prefix">₹</span>
      <input
        type="number"
        className="ra-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
      {suffix && <span className="ra-suffix">{suffix}</span>}
    </div>
  </div>
);

const PctField = ({ label, value, onChange, placeholder, testid }) => (
  <div className="ra-field">
    <label className="ra-label">{label}</label>
    <div className="ra-input-wrap">
      <input
        type="number"
        className="ra-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
      <span className="ra-suffix">%</span>
    </div>
  </div>
);

const OptionCard = ({ active, onClick, title, sub, testid }) => (
  <div
    className={`ra-opt ${active ? "on" : ""}`}
    onClick={onClick}
    data-testid={testid}
  >
    <div className="ra-opt-dot">{active && <i className="ti ti-check" aria-hidden="true" />}</div>
    <div>
      <div className="ra-opt-title">{title}</div>
      {sub && <div className="ra-opt-sub">{sub}</div>}
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════════
 * AI analysis → simple typed blocks (mirrors AssessmentResults parser)
 * ════════════════════════════════════════════════════════════════════════ */
const parseAi = (text) => {
  if (!text || typeof text !== "string") return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push({ type: "list", items: buf }); buf = []; } };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (/^#{1,3}\s+/.test(line)) { flush(); blocks.push({ type: "heading", text: line.replace(/^#{1,3}\s+/, "") }); }
    else if (/^(\*\*|__).+(\*\*|__)\s*:?$/.test(line)) { flush(); blocks.push({ type: "heading", text: line.replace(/[*_:]+/g, "").trim() }); }
    else if (/^(\d+\.|[-*•])\s+/.test(line)) { buf.push(line.replace(/^(\d+\.|[-*•])\s+/, "").replace(/\*\*/g, "")); }
    else { flush(); blocks.push({ type: "para", text: line.replace(/\*\*/g, "") }); }
  });
  flush();
  return blocks;
};

const scoreColor = (s) => (s >= 75 ? "#1D9E75" : s >= 50 ? "#BA7517" : "#A32D2D");

/* ════════════════════════════════════════════════════════════════════════
 * 1) PRE-IPO FINANCIAL ASSESSMENT — 6-step wizard → /assessment/calculate
 * ════════════════════════════════════════════════════════════════════════ */
const STEP_TITLES = ["Company Info", "P&L Data", "Balance Sheet", "Projections", "Governance", "Market Data"];

const DEFAULTS = {
  company_type: "", target_board: "", reporting_unit: "crores",
  year1_pat: "", year2_pat: "", year3_pat: "",
  year1_ebitda: "", year2_ebitda: "", year3_ebitda: "",
  year1_revenue: "", year2_revenue: "", year3_revenue: "",
  total_debt: "", total_cash: "",
  net_tangible_assets_y1: "", net_tangible_assets_y2: "", net_tangible_assets_y3: "",
  net_worth_y1: "", net_worth_y2: "", net_worth_y3: "",
  depreciation: "", capital_expenditure: "", working_capital_change: "",
  growth_rate: "", wacc: "", terminal_growth: "3",
  governance_answers: {},
  industry_pe: "", peer_pe: "", issue_type: "fresh", dilution_percent: "25",
};

const PreIPOWizard = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...DEFAULTS });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const reportRef = useRef(null);

  const unit = form.reporting_unit === "crores" ? "Cr" : "L";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGov = (id, v) => setForm((f) => ({ ...f, governance_answers: { ...f.governance_answers, [id]: v } }));
  const govAnswered = Object.keys(form.governance_answers).length;

  const valid = (s) => {
    switch (s) {
      case 1: return form.company_type && form.target_board;
      case 2: return ["year1_pat","year2_pat","year3_pat","year1_ebitda","year2_ebitda","year3_ebitda","year1_revenue","year2_revenue","year3_revenue"].every((k) => form[k] !== "");
      case 3: return ["total_debt","total_cash","net_tangible_assets_y1","net_tangible_assets_y2","net_tangible_assets_y3","net_worth_y1","net_worth_y2","net_worth_y3","depreciation","capital_expenditure","working_capital_change"].every((k) => form[k] !== "");
      case 4: return form.growth_rate !== "" && form.wacc !== "" && form.terminal_growth !== "";
      case 5: return govAnswered === GOVERNANCE_QUESTIONS.length;
      case 6: return form.industry_pe !== "" && form.peer_pe !== "" && form.dilution_percent !== "";
      default: return true;
    }
  };

  const next = () => {
    if (!valid(step)) {
      if (step === 5) toast.error(`Please answer all governance questions (${GOVERNANCE_QUESTIONS.length - govAnswered} remaining)`);
      else toast.error("Please fill all required fields");
      return;
    }
    if (step < 6) setStep(step + 1);
    else submit();
  };
  const back = () => { if (step > 1) setStep(step - 1); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const num = (k, d = 0) => parseFloat(form[k]) || d;
      const payload = {
        company_info: { company_type: form.company_type, target_board: form.target_board, reporting_unit: form.reporting_unit },
        pl_data: {
          year1_pat: num("year1_pat"), year2_pat: num("year2_pat"), year3_pat: num("year3_pat"),
          year1_ebitda: num("year1_ebitda"), year2_ebitda: num("year2_ebitda"), year3_ebitda: num("year3_ebitda"),
          year1_revenue: num("year1_revenue"), year2_revenue: num("year2_revenue"), year3_revenue: num("year3_revenue"),
        },
        balance_sheet: {
          total_debt: num("total_debt"), total_cash: num("total_cash"),
          net_tangible_assets_y1: num("net_tangible_assets_y1"), net_tangible_assets_y2: num("net_tangible_assets_y2"), net_tangible_assets_y3: num("net_tangible_assets_y3"),
          net_worth_y1: num("net_worth_y1"), net_worth_y2: num("net_worth_y2"), net_worth_y3: num("net_worth_y3"),
          depreciation: num("depreciation"), capital_expenditure: num("capital_expenditure"), working_capital_change: num("working_capital_change"),
        },
        projections: { growth_rate: num("growth_rate"), wacc: num("wacc"), terminal_growth: num("terminal_growth", 3) },
        governance_compliance: form.governance_answers,
        market_data: { industry_pe: num("industry_pe"), peer_pe: num("peer_pe") },
        issue_type: form.issue_type,
        dilution_percent: num("dilution_percent", 25),
      };
      const r = await axios.post(`${API_URL}/api/assessment/calculate`, payload, { withCredentials: true });
      setResult(r.data);
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      toast.success("Assessment completed!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to complete assessment. Please sign in and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const node = reportRef.current;
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: node.scrollWidth });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const iw = pw - 20;
      const ih = (canvas.height * iw) / canvas.width;
      let left = ih, pos = 10;
      pdf.addImage(img, "PNG", 10, pos, iw, ih, undefined, "FAST");
      left -= ph - 20;
      while (left > 0) { pos = left - ih + 10; pdf.addPage(); pdf.addImage(img, "PNG", 10, pos, iw, ih, undefined, "FAST"); left -= ph - 20; }
      pdf.save(`IPO_Readiness_Report_${form.target_board || "company"}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Report downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF. Please retry.");
    } finally { setDownloading(false); }
  };

  const reset = () => { setResult(null); setForm({ ...DEFAULTS }); setStep(1); };

  /* ───── Results report (full detailed, IPO Navigator skin) ───── */
  if (result) {
    const { readiness = {}, eligibility = {}, calculators = {}, governance = {}, valuation_summary = {}, ai_analysis, disclaimer } = result;
    const vs = valuation_summary || {};
    const band = vs.suggested_price_band || {};
    return (
      <div className="ra-results" data-testid="ra-financial-result">
        <div className="ra-results-bar">
          <div className="ra-results-title">IPO Readiness Report</div>
          <div className="ra-results-actions">
            <button className="f-btn" onClick={reset} data-testid="ra-new-assessment">New assessment</button>
            <button className="f-btn solid" onClick={downloadPdf} disabled={downloading} data-testid="ra-download-report">
              <i className="ti ti-download" aria-hidden="true" /> {downloading ? "Generating…" : "Download report (PDF)"}
            </button>
          </div>
        </div>

        <div ref={reportRef} className="ra-report">
          {/* Score header */}
          <div className="ra-score-head">
            <svg className="ra-score-ring" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E8EB" strokeWidth="12" />
              <circle cx="60" cy="60" r="50" fill="none" stroke={scoreColor(readiness.score || 0)} strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${((readiness.score || 0) / 100) * 314} 314`} transform="rotate(-90 60 60)" />
              <text x="60" y="58" textAnchor="middle" fontSize="26" fontWeight="600" fill="#1A1D21">{readiness.score || 0}</text>
              <text x="60" y="76" textAnchor="middle" fontSize="10" fill="#9BA3AD">/ 100</text>
            </svg>
            <div className="ra-score-info">
              <div className="ra-status-label" style={{ color: scoreColor(readiness.score || 0) }}>{readiness.status_label}</div>
              <div className="ra-status-msg">{readiness.status_message}</div>
              {readiness.issues?.length > 0 && (
                <ul className="ra-issues">
                  {readiness.issues.slice(0, 3).map((i, idx) => (
                    <li key={idx} className={i.severity === "critical" ? "crit" : "warn"}>
                      <i className={`ti ${i.severity === "critical" ? "ti-circle-x" : "ti-alert-triangle"}`} aria-hidden="true" /> {i.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="ra-improve-cta">
              <button className="ra-improve-btn" onClick={() => setContactOpen(true)} data-testid="ra-improve-score-btn">
                <i className="ti ti-rocket" aria-hidden="true" /> Want to Improve your Score
              </button>
              <div className="ra-improve-sub">Talk to IPOLabs — Team of Experts</div>
            </div>
            <ContactLeadDialog open={contactOpen} onOpenChange={setContactOpen} leadType="sales" />
          </div>

          {/* Valuation summary */}
          <div className="ra-stat-grid">
            <div className="ra-stat"><div className="ra-stat-l">Calculated Business Value</div><div className="ra-stat-v">₹{(vs.average_valuation || 0).toFixed(2)} Cr</div><div className="ra-stat-s">Average of P/E &amp; DCF</div></div>
            <div className="ra-stat"><div className="ra-stat-l">Suggested Price Band</div><div className="ra-stat-v">₹{(band.low || 0).toFixed(2)}–{(band.high || 0).toFixed(2)} Cr</div><div className="ra-stat-s">10–15% discount</div></div>
            <div className="ra-stat"><div className="ra-stat-l">Estimated Issue Size</div><div className="ra-stat-v">₹{(calculators.issue_size?.total_issue_size || 0).toFixed(2)} Cr</div><div className="ra-stat-s">{calculators.issue_size?.dilution_percent}% ({calculators.issue_size?.issue_type})</div></div>
          </div>

          {/* Calculators */}
          <div className="ra-sec-title">Valuation Analysis</div>
          <div className="ra-calc-grid">
            <CalcCard color="#185FA5" icon="ti-chart-bar" title="P/E Valuation" calc={calculators.pe_valuation} valueColor="#185FA5" />
            <CalcCard color="#3C3489" icon="ti-trending-up" title="DCF Valuation" calc={calculators.dcf_valuation} valueColor="#3C3489" />
            <CalcCard color="#0F6E56" icon="ti-file-text" title="Free Cash Flow to Equity" calc={calculators.fcfe} valueKey="fcfe" valueColor={(calculators.fcfe?.fcfe || 0) >= 0 ? "#0F6E56" : "#A32D2D"} />
            <CalcCard color="#854F0B" icon="ti-building-bank" title="Issue Size" calc={calculators.issue_size} valueKey="total_issue_size" valueColor="#854F0B" />
          </div>

          {/* SEBI Eligibility */}
          <div className="ra-card">
            <div className="ra-card-h"><i className="ti ti-license" aria-hidden="true" /> SEBI Eligibility — {eligibility.board} <span className="ra-card-sub">{eligibility.passed_count}/{eligibility.total_checks} met</span></div>
            {(eligibility.checks || []).map((c, i) => (
              <div key={i} className="ra-elig-row">
                <i className={`ti ${c.passed ? "ti-circle-check" : "ti-circle-x"}`} style={{ color: c.passed ? "#1D9E75" : "#A32D2D" }} aria-hidden="true" />
                <div className="ra-elig-text"><div className="ra-elig-crit">{c.criterion}</div><div className="ra-elig-meta">Required: {c.required} · Actual: {c.actual}</div></div>
                <span className={`ra-pill ${c.passed ? "ok" : "bad"}`}>{c.passed ? "Passed" : `Gap ₹${(c.gap || 0).toFixed(2)} Cr`}</span>
              </div>
            ))}
          </div>

          {/* Governance + breakdown */}
          {governance?.total > 0 && (
            <div className="ra-card">
              <div className="ra-card-h"><i className="ti ti-shield-check" aria-hidden="true" /> Governance &amp; Compliance <span className="ra-card-sub">{governance.positive_count}/{governance.total} compliant</span></div>
              <div className="ra-gov-summary">
                <div className="ra-gov-score" style={{ borderColor: scoreColor(governance.score) }}>
                  <span style={{ color: scoreColor(governance.score), fontSize: 24, fontWeight: 600 }}>{governance.score}</span><span style={{ fontSize: 10, color: "#9BA3AD" }}>/100</span>
                </div>
                <div className="ra-gov-cells">
                  <div className="ra-gov-cell ok"><b>{governance.positive_count}</b><span>Compliant</span></div>
                  <div className="ra-gov-cell bad"><b>{governance.negative_count}</b><span>Gaps</span></div>
                  <div className="ra-gov-cell"><b>{governance.total}</b><span>Total</span></div>
                </div>
              </div>
              {readiness.financial_score !== undefined && (
                <div className="ra-bd">
                  <Breakdown label="Financial Readiness (60%)" val={readiness.financial_score} color="#1D9E75" />
                  <Breakdown label="Governance & Compliance (40%)" val={readiness.governance_score} color="#185FA5" />
                  <div className="ra-bd-total"><span>Blended IPO Readiness Score</span><b>{readiness.score}/100</b></div>
                </div>
              )}
            </div>
          )}

          {/* AI gap analysis */}
          <div className="ra-ai">
            <div className="ra-ai-eyebrow"><i className="ti ti-sparkles" aria-hidden="true" /> Oracle Research Brief · AI-Powered Gap Analysis</div>
            <div className="ra-ai-body">
              {parseAi(ai_analysis).length === 0 ? (
                <p className="ra-ai-p">No detailed analysis was generated.</p>
              ) : parseAi(ai_analysis).map((b, i) => {
                if (b.type === "heading") return <div key={i} className="ra-ai-h">{b.text}</div>;
                if (b.type === "list") return <ol key={i} className="ra-ai-list">{b.items.map((it, j) => <li key={j}><span className="ra-ai-num">{j + 1}</span>{it}</li>)}</ol>;
                return <p key={i} className="ra-ai-p">{b.text}</p>;
              })}
            </div>
          </div>

          <div className="note" style={{ marginTop: 14 }}>
            <i className="ti ti-info-circle" style={{ fontSize: 13, color: "#BA7517" }} aria-hidden="true" />
            <div className="note-t">{disclaimer || "This IPO Readiness Assessment is a preliminary analysis based on the information provided and SEBI's general guidelines. It is not a substitute for professional advice."}</div>
          </div>
        </div>
      </div>
    );
  }

  /* ───── Wizard ───── */
  return (
    <div className="ra-wizard" data-testid="ra-financial-wizard">
      {/* Stepper */}
      <div className="ra-steps">
        {STEP_TITLES.map((t, i) => {
          const n = i + 1;
          return (
            <div key={t} className={`ra-step ${n === step ? "active" : n < step ? "done" : ""}`} data-testid={`ra-step-${n}`}>
              <div className="ra-step-dot">{n < step ? <i className="ti ti-check" aria-hidden="true" /> : n}</div>
              <span className="ra-step-label">{t}</span>
            </div>
          );
        })}
      </div>

      <div className="ra-panel">
        {step === 1 && (
          <>
            <div className="ra-panel-title">Company Information</div>
            <div className="ra-group-label">Company Type *</div>
            <div className="ra-opt-grid">
              {[
                { v: "private_limited", l: "Private Limited" },
                { v: "public_limited", l: "Public Limited" },
                { v: "llp", l: "LLP" },
                { v: "partnership_firm", l: "Partnership Firm" },
                { v: "sole_proprietorship", l: "Sole Proprietorship" },
                { v: "foreign_subsidiary", l: "Foreign Subsidiary" },
              ].map((o) => <OptionCard key={o.v} active={form.company_type === o.v} onClick={() => set("company_type", o.v)} title={o.l} testid={`ra-ct-${o.v}`} />)}
            </div>
            <div className="ra-group-label">Target Board *</div>
            <div className="ra-opt-grid two">
              <OptionCard active={form.target_board === "mainboard"} onClick={() => set("target_board", "mainboard")} title="Mainboard" sub="NSE / BSE Mainboard" testid="ra-tb-mainboard" />
              <OptionCard active={form.target_board === "sme"} onClick={() => set("target_board", "sme")} title="SME Board" sub="NSE Emerge / BSE SME" testid="ra-tb-sme" />
            </div>
            <div className="ra-group-label">Reporting Unit</div>
            <div className="ra-opt-grid two">
              <OptionCard active={form.reporting_unit === "crores"} onClick={() => set("reporting_unit", "crores")} title="Crores (₹ Cr)" testid="ra-ru-crores" />
              <OptionCard active={form.reporting_unit === "lacs"} onClick={() => set("reporting_unit", "lacs")} title="Lakhs (₹ L)" testid="ra-ru-lacs" />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="ra-panel-title">Profit &amp; Loss Data — last 3 years (in {unit})</div>
            <div className="ra-sub">Net Profit (PAT)</div>
            <div className="ra-grid-3">
              <NumField label="Year 1 (FY-2)" value={form.year1_pat} onChange={(v) => set("year1_pat", v)} suffix={unit} testid="ra-year1_pat" />
              <NumField label="Year 2 (FY-1)" value={form.year2_pat} onChange={(v) => set("year2_pat", v)} suffix={unit} testid="ra-year2_pat" />
              <NumField label="Year 3 (Current)" value={form.year3_pat} onChange={(v) => set("year3_pat", v)} suffix={unit} testid="ra-year3_pat" />
            </div>
            <div className="ra-sub">EBITDA</div>
            <div className="ra-grid-3">
              <NumField label="Year 1 (FY-2)" value={form.year1_ebitda} onChange={(v) => set("year1_ebitda", v)} suffix={unit} testid="ra-year1_ebitda" />
              <NumField label="Year 2 (FY-1)" value={form.year2_ebitda} onChange={(v) => set("year2_ebitda", v)} suffix={unit} testid="ra-year2_ebitda" />
              <NumField label="Year 3 (Current)" value={form.year3_ebitda} onChange={(v) => set("year3_ebitda", v)} suffix={unit} testid="ra-year3_ebitda" />
            </div>
            <div className="ra-sub">Revenue</div>
            <div className="ra-grid-3">
              <NumField label="Year 1 (FY-2)" value={form.year1_revenue} onChange={(v) => set("year1_revenue", v)} suffix={unit} testid="ra-year1_revenue" />
              <NumField label="Year 2 (FY-1)" value={form.year2_revenue} onChange={(v) => set("year2_revenue", v)} suffix={unit} testid="ra-year2_revenue" />
              <NumField label="Year 3 (Current)" value={form.year3_revenue} onChange={(v) => set("year3_revenue", v)} suffix={unit} testid="ra-year3_revenue" />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="ra-panel-title">Balance Sheet Data (in {unit})</div>
            <div className="ra-grid-2">
              <NumField label="Total Debt *" value={form.total_debt} onChange={(v) => set("total_debt", v)} suffix={unit} testid="ra-total_debt" />
              <NumField label="Total Cash *" value={form.total_cash} onChange={(v) => set("total_cash", v)} suffix={unit} testid="ra-total_cash" />
            </div>
            <div className="ra-sub">Net Tangible Assets (3 years)</div>
            <div className="ra-grid-3">
              <NumField label="Year 1 (FY-2)" value={form.net_tangible_assets_y1} onChange={(v) => set("net_tangible_assets_y1", v)} suffix={unit} testid="ra-nta1" />
              <NumField label="Year 2 (FY-1)" value={form.net_tangible_assets_y2} onChange={(v) => set("net_tangible_assets_y2", v)} suffix={unit} testid="ra-nta2" />
              <NumField label="Year 3 (Current)" value={form.net_tangible_assets_y3} onChange={(v) => set("net_tangible_assets_y3", v)} suffix={unit} testid="ra-nta3" />
            </div>
            <div className="ra-sub">Net Worth (3 years)</div>
            <div className="ra-grid-3">
              <NumField label="Year 1 (FY-2)" value={form.net_worth_y1} onChange={(v) => set("net_worth_y1", v)} suffix={unit} testid="ra-nw1" />
              <NumField label="Year 2 (FY-1)" value={form.net_worth_y2} onChange={(v) => set("net_worth_y2", v)} suffix={unit} testid="ra-nw2" />
              <NumField label="Year 3 (Current)" value={form.net_worth_y3} onChange={(v) => set("net_worth_y3", v)} suffix={unit} testid="ra-nw3" />
            </div>
            <div className="ra-grid-3">
              <NumField label="Depreciation (Annual) *" value={form.depreciation} onChange={(v) => set("depreciation", v)} suffix={unit} testid="ra-depreciation" />
              <NumField label="Capital Expenditure *" value={form.capital_expenditure} onChange={(v) => set("capital_expenditure", v)} suffix={unit} testid="ra-capex" />
              <NumField label="Change in Working Capital *" value={form.working_capital_change} onChange={(v) => set("working_capital_change", v)} suffix={unit} testid="ra-wcc" />
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className="ra-panel-title">Growth &amp; Projection Data</div>
            <div className="ra-grid-3">
              <PctField label="Expected Growth Rate (5-Yr) *" value={form.growth_rate} onChange={(v) => set("growth_rate", v)} placeholder="15" testid="ra-growth_rate" />
              <PctField label="WACC (Cost of Capital) *" value={form.wacc} onChange={(v) => set("wacc", v)} placeholder="12" testid="ra-wacc" />
              <PctField label="Terminal Growth Rate" value={form.terminal_growth} onChange={(v) => set("terminal_growth", v)} placeholder="3" testid="ra-terminal_growth" />
            </div>
            <div className="ra-info">These figures power the DCF valuation. Growth should reflect realistic 5-year expectations; WACC is typically 10–15% for Indian companies.</div>
          </>
        )}

        {step === 5 && (
          <>
            <div className="ra-panel-title">Governance &amp; Compliance Readiness <span className="ra-card-sub">{govAnswered}/{GOVERNANCE_QUESTIONS.length} answered</span></div>
            <div className="ra-gov-bar"><div style={{ width: `${(govAnswered / GOVERNANCE_QUESTIONS.length) * 100}%` }} /></div>
            {GOVERNANCE_CATEGORIES.map((cat) => (
              <div key={cat} className="ra-gov-cat">
                <div className="ra-gov-cat-h">{cat}</div>
                {GOVERNANCE_QUESTIONS.filter((q) => q.category === cat).map((q) => {
                  const a = form.governance_answers[q.id];
                  return (
                    <div key={q.id} className="ra-gov-q" data-testid={`ra-gc-${q.id}`}>
                      <div className="ra-gov-q-text">{q.question}</div>
                      <div className="ra-gov-q-btns">
                        {["yes", "no"].map((opt) => {
                          const on = a === opt;
                          const good = q.positiveAnswer === opt;
                          return (
                            <button key={opt} type="button" onClick={() => setGov(q.id, opt)}
                              className={`ra-yn ${on ? (good ? "good" : "bad") : ""}`} data-testid={`ra-gc-${q.id}-${opt}`}>
                              {on && <i className="ti ti-check" aria-hidden="true" />} {opt === "yes" ? "Yes" : "No"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}

        {step === 6 && (
          <>
            <div className="ra-panel-title">Market Data &amp; Issue Details</div>
            <div className="ra-grid-2">
              <div className="ra-field">
                <label className="ra-label">Industry Average P/E Ratio *</label>
                <div className="ra-input-wrap"><input type="number" className="ra-input" value={form.industry_pe} onChange={(e) => set("industry_pe", e.target.value)} placeholder="25" data-testid="ra-industry_pe" /><span className="ra-suffix">x</span></div>
              </div>
              <div className="ra-field">
                <label className="ra-label">Peer Group P/E Ratio *</label>
                <div className="ra-input-wrap"><input type="number" className="ra-input" value={form.peer_pe} onChange={(e) => set("peer_pe", e.target.value)} placeholder="22" data-testid="ra-peer_pe" /><span className="ra-suffix">x</span></div>
              </div>
            </div>
            <div className="ra-group-label">Issue Type *</div>
            <div className="ra-opt-grid three">
              <OptionCard active={form.issue_type === "fresh"} onClick={() => set("issue_type", "fresh")} title="Fresh Issue" sub="New shares issued" testid="ra-it-fresh" />
              <OptionCard active={form.issue_type === "ofs"} onClick={() => set("issue_type", "ofs")} title="OFS" sub="Offer for Sale" testid="ra-it-ofs" />
              <OptionCard active={form.issue_type === "both"} onClick={() => set("issue_type", "both")} title="Both" sub="Fresh + OFS" testid="ra-it-both" />
            </div>
            <div className="ra-group-label">Dilution Percentage *</div>
            <div className="ra-opt-grid">
              {["10", "15", "20", "25", "30", "35"].map((d) => (
                <OptionCard key={d} active={form.dilution_percent === d} onClick={() => set("dilution_percent", d)} title={`${d}%`} sub={d === "25" ? "SEBI min (Mainboard)" : null} testid={`ra-dil-${d}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer nav */}
      <div className="ra-foot">
        <button className="f-btn" onClick={back} disabled={step === 1} data-testid="ra-back">Back</button>
        <span className="ra-foot-step">Step {step} of 6 · {STEP_TITLES[step - 1]}</span>
        <button className="f-btn solid" onClick={next} disabled={submitting} data-testid="ra-next">
          {submitting ? "Calculating…" : step < 6 ? "Continue" : "Run Assessment"}
        </button>
      </div>
    </div>
  );
};

const CalcCard = ({ color, icon, title, calc, valueKey = "valuation", valueColor }) => {
  if (!calc) return null;
  const val = calc[valueKey];
  return (
    <div className="ra-calc" style={{ borderTopColor: color }}>
      <div className="ra-calc-h" style={{ color }}><i className={`ti ${icon}`} aria-hidden="true" /> {title}</div>
      {calc.warning ? (
        <div className="ra-calc-warn"><i className="ti ti-alert-triangle" aria-hidden="true" /> {calc.warning}</div>
      ) : (
        <>
          <div className="ra-calc-v" style={{ color: valueColor }}>₹{(val || 0).toFixed(2)} Cr</div>
          {calc.formula && <div className="ra-calc-f">{calc.formula}</div>}
        </>
      )}
    </div>
  );
};

const Breakdown = ({ label, val, color }) => (
  <div className="ra-bd-row">
    <div className="ra-bd-top"><span>{label}</span><b>{val}/100</b></div>
    <div className="ra-bd-bar"><div style={{ width: `${val}%`, background: color }} /></div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════════
 * 2) BSE SME / NSE EMERGE SELF-ASSESSMENT — checklist + branded PDF
 * ════════════════════════════════════════════════════════════════════════ */
// Some checkpoints are framed negatively (a "No" is the compliant answer) — flagged with `invert`.
const itemPass = (it, v) => (it.invert ? v === "no" : v === "yes");
const COMPANY_INFO = { name: "IPOLabs AI Pvt Ltd", email: "founders@ipo-labs.com", mobile: "+91 9967816957", logoPath: "/ipolabs-logo-full.png" };

// Categories removed from the BSE SME / NSE Emerge self-assessment (per product decision).
// Filtered at source so the score, "checkpoints passed" count and rendered sections re-align.
const HIDDEN_CATEGORIES = new Set([
  "Board / Net Worth",
  "Promoter Contribution & Lock-in",
  "Underwriting & Market Maker",
  "Monitoring Agency & Auditor Certificates",
  "DRHP Public Review",
  "DRHP Public Review & QR Code",
  "Special — Micro-Finance Companies (skip if not applicable)",
  "Insolvency / Winding-up",
  "Cooling-Off Period",
  "Investor Participation & Issue Structure",
  "Special — Broking Companies (skip if not applicable)",
]);

const computeAnalysis = (checklist, answers) => {
  const all = checklist.flatMap((sec) => sec.items.map((it) => ({ ...it, category: sec.category })));
  const total = all.length;
  const passed = all.filter((it) => itemPass(it, answers[it.id])).length;
  const failed = all.filter((it) => !itemPass(it, answers[it.id]));
  const score = total ? Math.round((passed / total) * 100) : 0;
  let band = "Not Ready", bandColor = "#A32D2D", bandHint = "Material gaps remain. Address the to-do list before initiating filings.";
  if (score >= 90) { band = "Filing-Ready"; bandColor = "#0F6E56"; bandHint = "Almost there. Verify the few remaining items with your merchant banker."; }
  else if (score >= 75) { band = "Substantially Ready"; bandColor = "#1D9E75"; bandHint = "Strong position. Close the open items and you're set."; }
  else if (score >= 50) { band = "Partially Ready"; bandColor = "#BA7517"; bandHint = "Significant work needed across multiple areas."; }
  return { total, passed, failed, score, band, bandColor, bandHint };
};

const SMESelfAssessmentPanel = () => {
  const [board, setBoard] = useState("bse");
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [reportDate] = useState(new Date());

  const checklist = (ELIGIBILITY_CHECKLISTS[board] || []).filter((sec) => !HIDDEN_CATEGORIES.has(sec.category));
  const analysis = useMemo(() => computeAnalysis(checklist, answers), [checklist, answers]);
  const setAnswer = (id, v) => setAnswers((p) => ({ ...p, [id]: v }));

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => document.getElementById("ra-sme-analysis")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const loadLogo = async () => {
    try {
      const res = await fetch(COMPANY_INFO.logoPath);
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.onerror = () => resolve(null); r.readAsDataURL(blob); });
    } catch { return null; }
  };

  const downloadPdf = async () => {
    if (!submitted) { toast.error("Please click 'Submit Assessment' first."); return; }
    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 40;
      let y = margin;
      const logoData = await loadLogo();
      const drawHeader = () => {
        if (logoData) { try { pdf.addImage(logoData, "PNG", margin, margin - 10, 70, 36); } catch {} }
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(20, 20, 20);
        pdf.text(COMPANY_INFO.name, pageW - margin, margin + 4, { align: "right" });
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(80, 80, 80);
        pdf.text(`Email: ${COMPANY_INFO.email}`, pageW - margin, margin + 18, { align: "right" });
        pdf.text(`Mobile: ${COMPANY_INFO.mobile}`, pageW - margin, margin + 30, { align: "right" });
        pdf.setDrawColor(220, 220, 220); pdf.line(margin, margin + 42, pageW - margin, margin + 42);
      };
      const ensure = (need) => { if (y + need > pageH - margin) { pdf.addPage(); y = margin; drawHeader(); y = margin + 56; } };
      drawHeader(); y = margin + 56;
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(16); pdf.setTextColor(15, 15, 15);
      pdf.text(`${BOARD_LABELS[board]} SME IPO — Eligibility Self Assessment`, margin, y); y += 18;
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(90, 90, 90);
      const sub = [companyName ? `Company: ${companyName}` : null, `Date: ${reportDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`].filter(Boolean);
      pdf.text(sub.join("   |   "), margin, y); y += 22;
      const boxH = 60;
      pdf.setFillColor(244, 246, 251); pdf.setDrawColor(220, 226, 236);
      pdf.roundedRect(margin, y, pageW - margin * 2, boxH, 6, 6, "FD");
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(28); pdf.setTextColor(20, 20, 20); pdf.text(`${analysis.score}%`, margin + 16, y + 38);
      pdf.setFontSize(13); pdf.text(analysis.band, margin + 90, y + 26);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
      pdf.text(`Passed: ${analysis.passed} of ${analysis.total}    |    Open Items: ${analysis.failed.length}`, margin + 90, y + 42);
      y += boxH + 18;
      pdf.setFont("helvetica", "italic"); pdf.setFontSize(10); pdf.setTextColor(70, 70, 70);
      const hint = pdf.splitTextToSize(analysis.bandHint, pageW - margin * 2); pdf.text(hint, margin, y); y += hint.length * 12 + 14;
      ensure(40);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(15, 15, 15);
      pdf.text(`To-Do Checklist (${analysis.failed.length} open items)`, margin, y); y += 14;
      pdf.setDrawColor(230, 230, 230); pdf.line(margin, y, pageW - margin, y); y += 12;
      if (analysis.failed.length === 0) {
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(50, 120, 50);
        pdf.text("All checkpoints satisfied. No open items.", margin, y); y += 16;
      } else {
        const grouped = {};
        analysis.failed.forEach((it) => { (grouped[it.category] = grouped[it.category] || []).push(it); });
        let idx = 1;
        Object.entries(grouped).forEach(([cat, items]) => {
          ensure(24); pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(30, 30, 60); pdf.text(cat, margin, y); y += 14;
          items.forEach((it) => {
            const lines = pdf.splitTextToSize(`${idx}. ${it.q}`, pageW - margin * 2 - 12);
            ensure(lines.length * 12 + 4);
            pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(50, 50, 50);
            pdf.setDrawColor(180, 30, 30); pdf.setFillColor(255, 240, 240); pdf.rect(margin, y - 8, 8, 8, "FD");
            pdf.text(lines, margin + 14, y); y += lines.length * 12 + 4; idx += 1;
          });
          y += 4;
        });
      }
      y += 10; ensure(30);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(15, 15, 15); pdf.text("Full Response Log", margin, y); y += 14;
      pdf.setDrawColor(230, 230, 230); pdf.line(margin, y, pageW - margin, y); y += 10;
      checklist.forEach((sec) => {
        ensure(20); pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(30, 30, 60); pdf.text(sec.category, margin, y); y += 12;
        sec.items.forEach((it) => {
          const ans = answers[it.id] === "yes" ? "YES" : "NO";
          const color = itemPass(it, answers[it.id]) ? [40, 130, 70] : [180, 30, 30];
          const lines = pdf.splitTextToSize(it.q, pageW - margin * 2 - 40); ensure(lines.length * 12 + 4);
          pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60); pdf.text(lines, margin + 4, y);
          pdf.setFont("helvetica", "bold"); pdf.setTextColor(...color); pdf.text(ans, pageW - margin, y, { align: "right" });
          y += lines.length * 12 + 4;
        });
        y += 4;
      });
      ensure(50); y = Math.max(y + 10, pageH - margin - 36);
      pdf.setDrawColor(220, 220, 220); pdf.line(margin, y, pageW - margin, y);
      pdf.setFont("helvetica", "italic"); pdf.setFontSize(8); pdf.setTextColor(110, 110, 110);
      const disc = "Disclaimer: This self-assessment is a preliminary indicative check based on the published BSE SME / NSE EMERGE criteria. It is not a substitute for professional legal, financial or merchant-banker advice. IPOLabs AI Pvt Ltd disclaims any liability arising from reliance on this report.";
      pdf.text(pdf.splitTextToSize(disc, pageW - margin * 2), margin, y + 14);
      pdf.save(`SME-Self-Assessment_${BOARD_LABELS[board].replace(/\s/g, "-")}_${reportDate.toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded.");
    } catch (e) { console.error(e); toast.error("Could not generate PDF. Please retry."); }
  };

  return (
    <div className="ra-sme" data-testid="ra-sme-panel">
      {/* Company name + board toggle */}
      <div className="ra-sme-controls">
        <div className="ra-field" style={{ flex: 1 }}>
          <label className="ra-label">Company name (for the report)</label>
          <div className="ra-input-wrap"><input type="text" className="ra-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g., Acme Industries Pvt Ltd" data-testid="ra-sme-company" /></div>
        </div>
        <div className="ra-sme-toggle">
          <button className={`stg ${board === "bse" ? "on" : ""}`} style={{ borderRadius: 10 }} onClick={() => { setBoard("bse"); setSubmitted(false); }} data-testid="ra-sme-bse">BSE SME</button>
          <button className={`stg ${board === "nse" ? "on" : ""}`} style={{ borderRadius: 10 }} onClick={() => { setBoard("nse"); setSubmitted(false); }} data-testid="ra-sme-nse">NSE EMERGE</button>
        </div>
      </div>

      {/* Checklist */}
      <div className="ra-sme-list" data-testid={`ra-sme-checklist-${board}`}>
        {checklist.map((sec) => (
          <div key={`${board}-${sec.category}`} className="ra-card">
            <div className="ra-card-h" style={{ marginBottom: 6 }}>{sec.category}</div>
            {sec.items.map((it) => {
              const val = answers[it.id];
              return (
                <div key={it.id} className="ra-gov-q" data-testid={`ra-sme-q-${it.id}`}>
                  <div className="ra-gov-q-text">{it.q}</div>
                  <div className="ra-gov-q-btns">
                    {["yes", "no"].map((opt) => (
                      <button key={opt} type="button" onClick={() => setAnswer(it.id, opt)}
                        className={`ra-yn ${val === opt ? ((it.invert ? opt === "no" : opt === "yes") ? "good" : "bad") : ""}`} data-testid={`ra-sme-ans-${it.id}-${opt}`}>
                        {val === opt && <i className="ti ti-check" aria-hidden="true" />} {opt === "yes" ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Sticky actions */}
      <div className="ra-sme-actions">
        <div className="ra-sme-status">{analysis.passed} of {analysis.total} checkpoints met{analysis.failed.length > 0 && ` · ${analysis.failed.length} open`}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="f-btn" onClick={() => { setAnswers({}); setSubmitted(false); }} data-testid="ra-sme-reset">Reset</button>
          <button className="f-btn solid" onClick={handleSubmit} data-testid="ra-sme-submit">Submit Assessment</button>
          <button className="f-btn solid" style={{ background: "#1D9E75", borderColor: "#1D9E75" }} onClick={downloadPdf} data-testid="ra-sme-download">
            <i className="ti ti-download" aria-hidden="true" /> Save PDF
          </button>
        </div>
      </div>

      {/* Analysis */}
      {submitted && (
        <div id="ra-sme-analysis" className="ra-sme-result" data-testid="ra-sme-result">
          <div className="ra-score-head">
            <div className="ra-sme-score">
              <div style={{ fontSize: 40, fontWeight: 600 }}>{analysis.score}<span style={{ fontSize: 20, color: "#9BA3AD" }}>%</span></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: analysis.bandColor }}>{analysis.band}</div>
            </div>
            <div className="ra-score-info">
              <div className="ra-status-msg">{analysis.bandHint}</div>
              <div className="ra-stat-s" style={{ marginTop: 6 }}>{analysis.passed}/{analysis.total} checkpoints passed against <b>{BOARD_LABELS[board]}</b> criteria.</div>
            </div>
          </div>

          <div className="ra-card">
            <div className="ra-card-h">
              <i className={`ti ${analysis.failed.length === 0 ? "ti-circle-check" : "ti-alert-triangle"}`} style={{ color: analysis.failed.length === 0 ? "#1D9E75" : "#BA7517" }} aria-hidden="true" />
              To-Do Checklist ({analysis.failed.length} open item{analysis.failed.length !== 1 ? "s" : ""})
            </div>
            {analysis.failed.length === 0 ? (
              <p className="ra-status-msg" style={{ color: "#0F6E56" }}>All checkpoints satisfied — your company appears aligned with {BOARD_LABELS[board]} eligibility criteria. Validate residual nuances with your merchant banker before filing.</p>
            ) : (
              Object.entries(analysis.failed.reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {})).map(([cat, items]) => (
                <div key={cat} className="ra-todo">
                  <div className="ra-todo-cat">{cat}</div>
                  <ul>{items.map((it) => <li key={it.id}><span>▢</span>{it.q}</li>)}</ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
 * Parent — mode toggle between the two flows
 * ════════════════════════════════════════════════════════════════════════ */
const ReadinessAssessmentDesk = ({ initialMode = "financial" }) => {
  const [mode, setMode] = useState(initialMode === "sme" ? "sme" : "financial");

  return (
    <div className="fp" data-testid="readiness-assessment-desk">
      <div className="fp-bar">
        <div className="fp-title">IPO readiness <span>score</span></div>
        <button className={`fp-pill ${mode === "financial" ? "pri" : ""}`} onClick={() => setMode("financial")} data-testid="ra-mode-financial">
          <i className="ti ti-calculator" aria-hidden="true" /> Pre-IPO Financial Assessment
        </button>
        <button className={`fp-pill ${mode === "sme" ? "pri" : ""}`} onClick={() => setMode("sme")} data-testid="ra-mode-sme">
          <i className="ti ti-checklist" aria-hidden="true" /> BSE SME / NSE Emerge Self-Assessment
        </button>
      </div>

      <div className="fp-body">
        {mode === "financial"
          ? <PreIPOWizard />
          : <SMESelfAssessmentPanel />}
      </div>
    </div>
  );
};

export default ReadinessAssessmentDesk;
